
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SuperAdminLicense, LicenseKey } from '@/types/licenseKey';
import { Search, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';

interface SuperAdminLicenseTableProps {
  filteredSuperAdmins: SuperAdminLicense[];
  licenseKeys: LicenseKey[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isVerifying: string | null;
  handleVerifyLicense: (superAdminId: string) => Promise<void>;
}

const SuperAdminLicenseTable: React.FC<SuperAdminLicenseTableProps> = ({
  filteredSuperAdmins,
  licenseKeys,
  searchQuery,
  setSearchQuery,
  isVerifying,
  handleVerifyLicense
}) => {
  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
        <div className="mt-4 md:mt-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search Super Admins..."
              className="pl-8 w-full md:w-64"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="text-left py-3 px-4 text-sm font-medium">Super Admin</th>
              <th className="text-left py-3 px-4 text-sm font-medium">Domain</th>
              <th className="text-left py-3 px-4 text-sm font-medium">License Plan</th>
              <th className="text-left py-3 px-4 text-sm font-medium">Status</th>
              <th className="text-left py-3 px-4 text-sm font-medium">Activated</th>
              <th className="text-left py-3 px-4 text-sm font-medium">Last Verified</th>
              <th className="text-left py-3 px-4 text-sm font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSuperAdmins.map((license) => {
              const key = licenseKeys.find(k => k.id === license.licenseKeyId);
              
              return (
                <tr key={license.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4">{key?.superAdminName || license.superAdminId}</td>
                  <td className="py-3 px-4">{license.domain}</td>
                  <td className="py-3 px-4 capitalize">
                    <Badge variant={
                      key?.plan === 'enterprise' ? 'default' :
                      key?.plan === 'expert' ? 'secondary' :
                      key?.plan === 'pro' ? 'outline' : 'destructive'
                    }>
                      {key?.plan || 'Unknown'}
                    </Badge>
                  </td>
                  <td className="py-3 px-4">
                    <Badge variant={license.isValid ? 'success' : 'destructive'}>
                      {license.isValid ? 'Valid' : 'Invalid'}
                    </Badge>
                  </td>
                  <td className="py-3 px-4">
                    {format(new Date(license.activatedAt), 'MMM d, yyyy')}
                  </td>
                  <td className="py-3 px-4">
                    {license.lastVerifiedAt ? 
                      format(new Date(license.lastVerifiedAt), 'MMM d, yyyy H:mm') : 
                      <span className="text-gray-400">Never</span>
                    }
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleVerifyLicense(license.superAdminId)}
                        disabled={isVerifying === license.superAdminId}
                      >
                        {isVerifying === license.superAdminId ? (
                          "Verifying..."
                        ) : (
                          <>
                            <CheckCircle2 className="mr-2 h-4 w-4" />
                            Verify
                          </>
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredSuperAdmins.length === 0 && (
              <tr>
                <td colSpan={7} className="py-6 text-center text-gray-500">
                  No Super Admin licenses found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SuperAdminLicenseTable;
